import React, { useEffect, useState } from 'react';
import {
  IStackTokens,
  Stack,
  Pivot,
  PivotItem,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  TextField,
  Dropdown,
  IDropdownOption,
  Text,
  Dialog,
  DialogFooter,
  DialogType,
  DefaultButton,
  PrimaryButton,
  Checkbox,
} from '@fluentui/react';
import { Button } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { PieChart } from '@fluentui/react-charting';
import type { AcademyStatus, EmployeeStatus } from 'src/models/employeeStatus';
import { getReportProgress } from 'src/endpoints/getReportProgress';
import { useEmployee } from '../Contexts/EmployeeContext';
import { sendProfessorsEmail } from 'src/endpoints/sendProfessorsEmail';

type ViewType = 'Faltan de subir' | 'Completado';

const stackTokens: IStackTokens = {
  childrenGap: 20,
  padding: 20,
};

const stageOptions: IDropdownOption[] = [
  { key: '1', text: '1' },
  { key: '2', text: '2' },
  { key: '3', text: '3' },
  { key: '4', text: 'Forma evaluación' },
];

const viewOptions: IDropdownOption[] = [
  { key: 'Faltan de subir', text: 'Faltan de subir' },
  { key: 'Completado', text: 'Completado' },
];

const columns: IColumn[] = [
  {
    key: 'column1',
    name: 'Numero de empleado',
    fieldName: 'EmployeeId',
    minWidth: 150,
    maxWidth: 300,
    isResizable: true,
  },
  {
    key: 'column2',
    name: 'Nombre',
    fieldName: 'Name',
    minWidth: 150,
    maxWidth: 300,
    isResizable: true,
  },
  {
    key: 'column3',
    name: 'Correo',
    fieldName: 'Email',
    minWidth: 200,
    maxWidth: 350,
    isResizable: true,
  },
];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [academies, setAcademies] = useState<AcademyStatus[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedStageFilter, setSelectedStageFilter] = useState<string>('');
  const [selectedView, setSelectedView] = useState<ViewType>('Faltan de subir');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const { employee } = useEmployee();

  // State for the email dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [emailContent, setEmailContent] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  // Track which academy (pivot) is selected
  const [selectedAcademyIndex, setSelectedAcademyIndex] = useState(0);
  const [employeesFiltered, setEmployeesFiltered] = useState<EmployeeStatus[]>(
    []
  );

  // Compute current filtered employees count and academy name
  const [currentCount, setCurrentCount] = useState(0);
  const [currentAcademyName, setCurrentAcademyName] = useState('');

  useEffect(() => {
    const fetchReportProgressData = async () => {
      try {
        const response = await getReportProgress();
        let academiesData: AcademyStatus[] = response.data.Academies;
        // If the employee is a coordinator (Role === 2), show only their academy.

        academiesData = academiesData.filter(
          (academy) => academy.AcademyName.trim() !== ''
        );

        if (employee && employee.Role === 2 && employee.Academy) {
          academiesData = academiesData.filter(
            (academy) => academy.AcademyName === employee.Academy
          );
        }
        setAcademies(academiesData);
      } catch (err: any) {
        setError('Error fetching report progress data.');
        console.error('Error fetching report progress data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReportProgressData();
  }, [employee]);

  const onSearchChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    const value: string = newValue || '';
    setTimeout(() => {
      setSearchText(value);
    }, 300);
  };

  useEffect(() => {
    if (selectedStageFilter && academies.length > 0) {
      const currentAcademy = academies[selectedAcademyIndex];
      setCurrentAcademyName(currentAcademy.AcademyName);
      const employeesProgress: EmployeeStatus[] =
        selectedView === 'Faltan de subir'
          ? currentAcademy.Missing[selectedStageFilter] || []
          : currentAcademy.Completed[selectedStageFilter] || [];
      const filteredEmployees = employeesProgress.filter(
        (emp: EmployeeStatus) =>
          emp.Name.toLowerCase().includes(searchText.toLowerCase()) ||
          emp.EmployeeId.toLowerCase().includes(searchText.toLowerCase()) ||
          emp.Email.toLowerCase().includes(searchText.toLowerCase())
      );
      setCurrentCount(filteredEmployees.length);
      setEmployeesFiltered(filteredEmployees);
    }
  }, [
    selectedStageFilter,
    academies,
    selectedView,
    searchText,
    selectedAcademyIndex,
  ]);

  // Default email content with template
  const defaultEmailContent = `Hola,

Este es un recordatorio para que suban su progreso ${
    selectedStageFilter === '4' ? 'Forma evaluación' : selectedStageFilter
  }, favor de subirlo lo más pronto posible.

Escolar FCFM`;

  // Opens the dialog and sets default values
  const openEmailDialog = () => {
    if (!selectedStageFilter) {
      alert('Por favor, selecciona un filtro de avance.');
      return;
    }
    setEmailContent(defaultEmailContent);
    setConfirmSend(false);
    setIsDialogOpen(true);
  };

  // Handle sending the email (e.g., call an API endpoint)
  const handleSendEmail = async () => {
    // Here you can call your API endpoint to send the email.
    sendProfessorsEmail(
      employeesFiltered.map((emp) => emp.Email),
      emailContent
    );
    // After sending, close the dialog.
    setIsDialogOpen(false);
  };

  return (
    <Stack tokens={stackTokens}>
      <Stack horizontal horizontalAlign="space-between">
        {employee && employee.Role !== 3 && (
          <Button
            appearance="secondary"
            size="large"
            style={{ backgroundColor: '#f5f5f5', border: '1px solid #ccc' }}
            onClick={() => navigate('/')}
          >
            Volver
          </Button>
        )}
        {employee && employee.Role === 3 && (
          <Button
            appearance="secondary"
            size="large"
            style={{ backgroundColor: '#f5f5f5', border: '1px solid #ccc' }}
            onClick={() => navigate('/admin')}
          >
            Administrador
          </Button>
        )}
        <Button
          appearance="secondary"
          size="large"
          style={{ backgroundColor: '#f5f5f5', border: '1px solid #ccc' }}
          onClick={() => {
            if (currentCount > 0) {
              openEmailDialog();
            } else {
              alert('No hay profesores en el filtro seleccionado.');
            }
          }}
        >
          Enviar correo a profesores
        </Button>
      </Stack>

      <Stack horizontalAlign="center">
        <Text variant="xLarge">Dashboard</Text>
      </Stack>

      {isLoading ? (
        <Text variant="large">Cargando...</Text>
      ) : error ? (
        <Text style={{ color: 'red' }}>{error}</Text>
      ) : (
        <Pivot
          onLinkClick={(item) =>
            setSelectedAcademyIndex(parseInt(item!.props.itemKey || '0'))
          }
        >
          {academies.map((academy, index) => (
            <PivotItem
              headerText={academy.AcademyName}
              itemKey={index.toString()}
              key={index}
            >
              <Stack tokens={{ childrenGap: 20 }}>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                  <TextField
                    placeholder="Buscar el nombre, correo o numero de empleado, se toman en cuenta los acentos"
                    onChange={onSearchChange}
                    styles={{ root: { marginBottom: 20, flex: 1 } }}
                  />
                  <Dropdown
                    placeholder="Filtrar por numero de avance"
                    options={stageOptions}
                    onChange={(_event, option) =>
                      setSelectedStageFilter(option?.key as string)
                    }
                    styles={{ root: { marginBottom: 20, width: 300 } }}
                    selectedKey={selectedStageFilter}
                  />
                  <Dropdown
                    placeholder="Seleccionar vista"
                    options={viewOptions}
                    onChange={(_event, option) =>
                      setSelectedView(option?.key as ViewType)
                    }
                    styles={{ root: { marginBottom: 20, width: 300 } }}
                    selectedKey={selectedView}
                  />
                </Stack>

                <Stack tokens={{ childrenGap: 50 }}>
                  {selectedStageFilter && (
                    <Stack horizontalAlign="center">
                      {(() => {
                        const missingCount: number =
                          academy.Missing[selectedStageFilter]?.length || 0;
                        const completedCount: number =
                          academy.Completed[selectedStageFilter]?.length || 0;

                        const chartData = [
                          {
                            x: 'Faltan de subir',
                            y: missingCount,
                          },
                          {
                            x: 'Completado',
                            y: completedCount,
                          },
                        ];

                        return (
                          <Stack tokens={{ childrenGap: 30 }}>
                            <PieChart
                              data={chartData}
                              colors={['#f1bbbc', '#a3d9a5']}
                              width={400}
                              height={400}
                              chartTitle={`Profesores que han subido y faltan de subir ${
                                selectedStageFilter === '4'
                                  ? 'alguna forma evaluación'
                                  : `algún avance ${selectedStageFilter}`
                              }`}
                            />
                            <Text>
                              Faltan de subir: {missingCount} - Completado:{' '}
                              {completedCount}
                            </Text>
                          </Stack>
                        );
                      })()}
                    </Stack>
                  )}

                  <Stack tokens={{ childrenGap: 20 }}>
                    {!selectedStageFilter ? (
                      // If no stage filter is selected, show the "please select a filter" message
                      <Text variant="xLarge">
                        Selecciona un filtro (Academia y número de avance) para
                        ver los empleados que faltan de subir algún avance en
                        sus materias.
                      </Text>
                    ) : (
                      // If a stage filter is selected, show the chart/list logic
                      (() => {
                        const employeesProgress: EmployeeStatus[] =
                          selectedView === 'Faltan de subir'
                            ? academy.Missing[selectedStageFilter] || []
                            : academy.Completed[selectedStageFilter] || [];

                        const employeesFiltered = employeesProgress.filter(
                          (emp: EmployeeStatus) =>
                            emp.Name.toLowerCase().includes(
                              searchText.toLowerCase()
                            ) ||
                            emp.EmployeeId.toLowerCase().includes(
                              searchText.toLowerCase()
                            ) ||
                            emp.Email.toLowerCase().includes(
                              searchText.toLowerCase()
                            )
                        );

                        // If the filtered list has items, show them. Otherwise, show "no employees" message.
                        return employeesFiltered.length > 0 ? (
                          <DetailsList
                            items={employeesFiltered}
                            columns={columns}
                            setKey="set"
                            selectionMode={0}
                            layoutMode={DetailsListLayoutMode.justified}
                            selectionPreservedOnEmptyClick
                            ariaLabelForSelectionColumn="Toggle selection"
                            checkButtonAriaLabel="select row"
                          />
                        ) : (
                          <Text variant="xLarge">
                            No se encontraron empleados para el filtro
                            seleccionado.
                          </Text>
                        );
                      })()
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </PivotItem>
          ))}
        </Pivot>
      )}

      {/* Dialog for email confirmation */}
      <Dialog
        hidden={!isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Confirmar envío de correo',
          subText: `Al clic en enviar se enviará un correo a los ${currentCount} empleados que están en el filtro para ${currentAcademyName} y ${
            selectedStageFilter === '4'
              ? 'Forma evaluación'
              : 'progreso ' + selectedStageFilter
          }.`,
        }}
      >
        <Stack tokens={{ childrenGap: 20 }}>
          <TextField
            label="Contenido del correo"
            multiline
            rows={7}
            value={emailContent}
            onChange={(_e, newValue) => setEmailContent(newValue || '')}
          />
          <Checkbox
            label="Confirmo que deseo enviar el correo"
            checked={confirmSend}
            onChange={(_e, checked) => setConfirmSend(!!checked)}
          />
          <DialogFooter>
            <DefaultButton
              onClick={() => setIsDialogOpen(false)}
              text="Cancelar"
            />
            <PrimaryButton
              onClick={handleSendEmail}
              text="Enviar"
              disabled={!confirmSend || currentCount === 0}
            />
          </DialogFooter>
        </Stack>
      </Dialog>
    </Stack>
  );
};

export default Dashboard;
